import { z } from "zod";
import { oc } from "@orpc/contract";
//#region src/api/orpc/questV2.ts
const translationsSchema = z.object({
	zh: z.string(),
	ja: z.string(),
	ko: z.string(),
	en: z.string()
});
const ButtonAction = {
	FOLLOW_URL: "follow_url",
	CLAIM_REWARD: "claim_reward",
	PARTICIPATE_SURVEY: "participate_survey",
	OPEN_SOCIAL_FOLLOW_MODAL: "open_social_follow_modal",
	SHOW_INCENTIVE_ADS: "show_incentive_ads",
	SHOW_GMO_OFFERWALL: "show_gmo_offerwall",
	OPEN_DOWNLOAD_APP_MODAL: "open_download_app_modal",
	DAILY_CLAIM: "daily_claim",
	SHARE_ACTIVITY: "share_activity",
	MAINPAGE_DAILY_CLAIM: "mainpage_daily_claim"
};
const ButtonActions = Object.values(ButtonAction);
const buttonActionTypeSchema = z.enum(ButtonActions);
const QuestInterval = {
	DAY: "day",
	WEEK: "week",
	MONTH: "month",
	YEAR: "year",
	ONE_TIME: "one-time"
};
const QuestIntervals = Object.values(QuestInterval);
const questIntervalSchema = z.enum(QuestIntervals);
const QuestVisibility = {
	VISIBLE: "visible",
	INVISIBLE: "invisible"
};
const QuestVisibilities = Object.values(QuestVisibility);
const questVisibilitySchema = z.enum(QuestVisibilities);
const QuestVerificationRequirement = {
	NONE: "none",
	REQUIRED: "required"
};
const QuestVerificationRequirementValues = Object.values(QuestVerificationRequirement);
const questVerificationRequirementSchema = z.enum(QuestVerificationRequirementValues);
const buttonActionConfigSchemas = {
	[ButtonAction.FOLLOW_URL]: z.strictObject({
		linkKey: z.string(),
		link: translationsSchema.optional(),
		nativeLinkKey: z.string().optional(),
		nativeLink: translationsSchema.optional(),
		persistAfterComplete: z.boolean().default(false)
	}),
	[ButtonAction.CLAIM_REWARD]: z.strictObject({}),
	[ButtonAction.PARTICIPATE_SURVEY]: z.strictObject({ formId: z.string() }),
	[ButtonAction.OPEN_SOCIAL_FOLLOW_MODAL]: z.strictObject({}),
	[ButtonAction.SHOW_INCENTIVE_ADS]: z.strictObject({}),
	[ButtonAction.SHOW_GMO_OFFERWALL]: z.strictObject({}),
	[ButtonAction.OPEN_DOWNLOAD_APP_MODAL]: z.strictObject({}),
	[ButtonAction.DAILY_CLAIM]: z.strictObject({}),
	[ButtonAction.SHARE_ACTIVITY]: z.strictObject({}),
	[ButtonAction.MAINPAGE_DAILY_CLAIM]: z.strictObject({})
};
const buttonActionConfigSchema = z.union([
	buttonActionConfigSchemas[ButtonAction.FOLLOW_URL],
	buttonActionConfigSchemas[ButtonAction.CLAIM_REWARD],
	buttonActionConfigSchemas[ButtonAction.PARTICIPATE_SURVEY],
	buttonActionConfigSchemas[ButtonAction.OPEN_SOCIAL_FOLLOW_MODAL],
	buttonActionConfigSchemas[ButtonAction.SHOW_INCENTIVE_ADS],
	buttonActionConfigSchemas[ButtonAction.SHOW_GMO_OFFERWALL],
	buttonActionConfigSchemas[ButtonAction.OPEN_DOWNLOAD_APP_MODAL],
	buttonActionConfigSchemas[ButtonAction.DAILY_CLAIM],
	buttonActionConfigSchemas[ButtonAction.SHARE_ACTIVITY],
	buttonActionConfigSchemas[ButtonAction.MAINPAGE_DAILY_CLAIM]
]).nullable();
const buttonActionUnionSchema = z.discriminatedUnion("buttonAction", [
	z.object({
		buttonAction: z.literal(ButtonAction.FOLLOW_URL),
		buttonActionConfig: buttonActionConfigSchemas[ButtonAction.FOLLOW_URL]
	}),
	z.object({
		buttonAction: z.literal(ButtonAction.CLAIM_REWARD),
		buttonActionConfig: buttonActionConfigSchemas[ButtonAction.CLAIM_REWARD]
	}),
	z.object({
		buttonAction: z.literal(ButtonAction.PARTICIPATE_SURVEY),
		buttonActionConfig: buttonActionConfigSchemas[ButtonAction.PARTICIPATE_SURVEY]
	}),
	z.object({
		buttonAction: z.literal(ButtonAction.OPEN_SOCIAL_FOLLOW_MODAL),
		buttonActionConfig: buttonActionConfigSchemas[ButtonAction.OPEN_SOCIAL_FOLLOW_MODAL]
	}),
	z.object({
		buttonAction: z.literal(ButtonAction.SHOW_INCENTIVE_ADS),
		buttonActionConfig: buttonActionConfigSchemas[ButtonAction.SHOW_INCENTIVE_ADS]
	}),
	z.object({
		buttonAction: z.literal(ButtonAction.SHOW_GMO_OFFERWALL),
		buttonActionConfig: buttonActionConfigSchemas[ButtonAction.SHOW_GMO_OFFERWALL]
	}),
	z.object({
		buttonAction: z.literal(ButtonAction.OPEN_DOWNLOAD_APP_MODAL),
		buttonActionConfig: buttonActionConfigSchemas[ButtonAction.OPEN_DOWNLOAD_APP_MODAL]
	}),
	z.object({
		buttonAction: z.literal(ButtonAction.DAILY_CLAIM),
		buttonActionConfig: buttonActionConfigSchemas[ButtonAction.DAILY_CLAIM]
	}),
	z.object({
		buttonAction: z.literal(ButtonAction.SHARE_ACTIVITY),
		buttonActionConfig: buttonActionConfigSchemas[ButtonAction.SHARE_ACTIVITY]
	}),
	z.object({
		buttonAction: z.literal(ButtonAction.MAINPAGE_DAILY_CLAIM),
		buttonActionConfig: buttonActionConfigSchemas[ButtonAction.MAINPAGE_DAILY_CLAIM]
	})
]);
function refineButtonActionConfig(data, ctx) {
	if (data.buttonActionConfig == null) return;
	const result = buttonActionConfigSchemas[data.buttonAction].safeParse(data.buttonActionConfig);
	if (!result.success) for (const issue of result.error.issues) ctx.addIssue({
		...issue,
		path: ["buttonActionConfig", ...issue.path]
	});
}
const questV2StatusSchema = z.enum([
	"ongoing",
	"unclaimed",
	"rewarded"
]);
const questV2ProgressSchema = z.object({
	current: z.number(),
	rewarded: z.number(),
	total: z.number()
}).nullable();
const questV2BaseFieldsSchema = z.object({
	id: z.string(),
	title: translationsSchema,
	description: translationsSchema.nullable(),
	button: translationsSchema,
	titleKey: z.string(),
	buttonKey: z.string(),
	icon: z.string().nullable(),
	iconMediaId: z.string().nullable(),
	categoryId: z.string().nullable(),
	rewards: z.string().nullable(),
	status: questV2StatusSchema,
	progress: questV2ProgressSchema,
	buttonEnabled: z.boolean(),
	verificationRequirement: questVerificationRequirementSchema,
	startAt: z.iso.datetime().nullable(),
	endAt: z.iso.datetime().nullable(),
	createdAt: z.iso.datetime(),
	visibility: questVisibilitySchema.optional()
});
const questV2Schema = z.intersection(questV2BaseFieldsSchema, buttonActionUnionSchema);
const eventQuestV2Schema = z.intersection(questV2BaseFieldsSchema.extend({ interval: questIntervalSchema }), buttonActionUnionSchema);
const questV2CategorySchema = z.object({
	id: z.string(),
	labelKey: z.string(),
	label: translationsSchema
});
const questV2Contract = oc.prefix("/quest-v2").tag("QuestV2").router({
	listCategories: oc.route({
		method: "GET",
		path: "/categories",
		summary: "List quest categories"
	}).output(z.object({ data: z.array(questV2CategorySchema) })),
	listQuests: oc.route({
		method: "GET",
		path: "/quests",
		summary: "List quests for the current user"
	}).input(z.object({ invisibleQuests: z.enum(["include", "exclude"]).optional() })).output(z.object({ data: z.array(questV2Schema) })),
	claimReward: oc.route({
		method: "POST",
		path: "/quests/{questId}/claim",
		summary: "Claim reward for a completed quest"
	}).input(z.object({ questId: z.string() })).output(z.object({ success: z.literal(true) })).errors({
		QUEST_NOT_FOUND: {
			status: 404,
			message: "Quest not found"
		},
		QUEST_NOT_CLAIMABLE: {
			status: 400,
			message: "Quest is not in a claimable state"
		}
	}),
	reportUrlVisit: oc.route({
		method: "POST",
		path: "/report-visit",
		summary: "Report that the user visited a quest URL, advancing progress"
	}).input(z.object({ url: z.string() })).output(z.object({ success: z.literal(true) })),
	reportSocialFollow: oc.route({
		method: "POST",
		path: "/report-social-follow",
		summary: "Report that the user followed a social platform, advancing progress"
	}).input(z.object({ platform: z.string().trim().min(1).transform((v) => v.toLowerCase()) })).output(z.object({ success: z.literal(true) })),
	listFollowedPlatforms: oc.route({
		method: "GET",
		path: "/followed-platforms",
		summary: "List platforms the current user has followed"
	}).output(z.object({ data: z.array(z.string()) })),
	listEventQuests: oc.route({
		method: "GET",
		path: "/event-quests",
		summary: "List event quests for the current user"
	}).output(z.object({ data: z.array(eventQuestV2Schema) }))
});
//#endregion
export { ButtonAction, QuestInterval, QuestVerificationRequirement, QuestVisibility, buttonActionConfigSchema, buttonActionConfigSchemas, buttonActionTypeSchema, buttonActionUnionSchema, questIntervalSchema, questV2Contract, questV2Schema, questVerificationRequirementSchema, questVisibilitySchema, refineButtonActionConfig, translationsSchema };
